import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash, Warning, X } from '@phosphor-icons/react';
import { listMyOrganizations, softDeleteOrganization } from '@/api/orgs';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormSkeleton } from '@/components/ui/form-skeleton';

// DangerZone hosts owner-only destructive actions. Today: soft-delete the
// whole organization with 30-day restore. Tomorrow: maybe transfer ownership,
// hard delete with GDPR erasure, etc. Each action lives in its own card so
// the page can grow without losing the visual "this is the scary section"
// framing.
//
// The page assumes the caller is the owner — the SettingsPanel tile is
// hidden from non-owners, and the backend rejects with 403 not_owner if a
// non-owner reaches the endpoint anyway. We still render a friendly error
// state instead of crashing if the user lands here directly via URL.
export function DangerZone() {
  const { slug = '' } = useParams();
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;

  const orgsQuery = useQuery({
    queryKey: ['my-orgs'],
    queryFn: () => listMyOrganizations(token),
    enabled: !!token,
  });
  const org = orgsQuery.data?.find((o) => o.slug === slug);

  const [confirmOpen, setConfirmOpen] = useState(false);

  if (orgsQuery.isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-4 md:p-8">
        <FormSkeleton rows={3} action={false} />
      </div>
    );
  }

  if (!org) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-4 md:p-8">
        <p className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-foreground">
          {t('settings.dangerZone.notFound')}
        </p>
      </div>
    );
  }

  if (org.role !== 'owner') {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-4 md:p-8">
        <p className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-foreground">
          {t('settings.dangerZone.notOwner')}
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-4 md:p-8">
      <header className="flex flex-col gap-1">
        <p className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('settings.title')}
        </p>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight md:text-4xl">
          {t('settings.dangerZone.title')}
        </h1>
        <p className="text-muted-foreground">{t('settings.dangerZone.subtitle')}</p>
      </header>

      <section className="flex flex-col gap-4 rounded-2xl border border-destructive/40 bg-background p-6 shadow-sm">
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-destructive/10 text-destructive"
          >
            <Warning className="h-5 w-5" weight="fill" />
          </span>
          <div className="flex flex-col gap-1">
            <h2 className="font-heading text-lg font-bold">
              {t('settings.dangerZone.delete.title')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('settings.dangerZone.delete.body', { days: 30 })}
            </p>
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            type="button"
            variant="destructive"
            onClick={() => setConfirmOpen(true)}
          >
            <Trash className="h-4 w-4" />
            {t('settings.dangerZone.delete.cta')}
          </Button>
        </div>
      </section>

      <ConfirmDeleteDialog
        open={confirmOpen}
        slug={slug}
        orgName={org.name}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
}

interface ConfirmDeleteDialogProps {
  open: boolean;
  slug: string;
  orgName: string;
  onClose: () => void;
}

// ConfirmDeleteDialog uses the native <dialog> element (focus trap, esc
// to close, modal backdrop), matching the pattern of AddMemberModal. The
// destructive button is disabled until the user types the org's name
// verbatim — case- and whitespace-sensitive. The server re-checks the
// match so a SPA bug can't bypass the gate.
function ConfirmDeleteDialog({ open, slug, orgName, onClose }: ConfirmDeleteDialogProps) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [typed, setTyped] = useState('');
  const [errorCode, setErrorCode] = useState<string | null>(null);

  // Reset state on every open. Keeps a half-typed name from leaking into
  // the next confirmation attempt. Auto-focus the input so the user can
  // type immediately.
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) {
      setTyped('');
      setErrorCode(null);
      el.showModal();
      // Defer focus until after the dialog actually paints.
      requestAnimationFrame(() => inputRef.current?.focus());
    } else if (!open && el.open) {
      el.close();
    }
  }, [open]);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    const handleClose = () => onClose();
    el.addEventListener('close', handleClose);
    return () => el.removeEventListener('close', handleClose);
  }, [onClose]);

  const mutation = useMutation({
    mutationFn: () => softDeleteOrganization(token, slug, typed),
    onSuccess: () => {
      // Org list and any cached data for this org are stale. Invalidate
      // both, then route the user back to the landing page where the
      // soft-deleted org will appear in the "Pending deletion" section.
      void queryClient.invalidateQueries({ queryKey: ['my-orgs'] });
      void queryClient.invalidateQueries({ queryKey: ['my-orgs-pending-deletion'] });
      onClose();
      navigate('/');
    },
    onError: (err) => {
      setErrorCode(err instanceof ApiError ? err.code : 'unknown');
    },
  });

  const matches = typed === orgName;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!matches || mutation.isPending) return;
    setErrorCode(null);
    mutation.mutate();
  }

  return (
    <dialog
      ref={dialogRef}
      className="rounded-2xl border border-border bg-background p-0 shadow-2xl backdrop:bg-foreground/40"
    >
      <form onSubmit={onSubmit} noValidate className="flex w-[min(32rem,90vw)] flex-col gap-4 p-6">
        <header className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <h2 className="font-heading text-xl font-bold text-foreground">
              {t('settings.dangerZone.confirm.title')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('settings.dangerZone.confirm.body', { name: orgName, days: 30 })}
            </p>
          </div>
          <button
            type="button"
            aria-label={t('common.cancel')}
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-pill text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </header>

        <label className="flex flex-col gap-2 text-sm font-medium text-foreground">
          {t('settings.dangerZone.confirm.label', { name: orgName })}
          <Input
            ref={inputRef}
            type="text"
            value={typed}
            onChange={(e) => {
              setTyped(e.target.value);
              setErrorCode(null);
            }}
            autoComplete="off"
            spellCheck={false}
            aria-invalid={typed.length > 0 && !matches ? true : undefined}
          />
        </label>

        {errorCode && (
          <p role="alert" className="text-sm text-destructive">
            {t(`settings.dangerZone.error.${errorCode}`, {
              defaultValue: t('settings.dangerZone.error.unknown'),
            })}
          </p>
        )}

        <footer className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="destructive"
            disabled={!matches || mutation.isPending}
          >
            <Trash className="h-4 w-4" />
            {mutation.isPending
              ? t('settings.dangerZone.confirm.submitting')
              : t('settings.dangerZone.confirm.submit')}
          </Button>
        </footer>
      </form>
    </dialog>
  );
}
